import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Check, Loader2 } from 'lucide-react';
import { CURRENT_PRIVACY_POLICY_VERSION } from '@/utils/privacyPolicyVersion';
import { scrollToFirstError } from '@/utils/scrollToFirstError';

// Phone country code -> currency mapping (Bölüm 4 spec)
const COUNTRY_CODE_TO_CURRENCY = {
  '+971': 'AED',
  '+966': 'SAR',
  '+965': 'KWD',
  '+973': 'BHD',
  '+968': 'OMR',
  '+974': 'QAR',
};

const COUNTRY_CODES = [
  { code: '+971', flag: '🇦🇪', country: 'UAE' },
  { code: '+966', flag: '🇸🇦', country: 'Saudi Arabia' },
  { code: '+965', flag: '🇰🇼', country: 'Kuwait' },
  { code: '+973', flag: '🇧🇭', country: 'Bahrain' },
  { code: '+968', flag: '🇴🇲', country: 'Oman' },
  { code: '+974', flag: '🇶🇦', country: 'Qatar' },
];

const COUNTRIES_OF_RESIDENCE = [
  'UAE',
  'Saudi Arabia',
  'Kuwait',
  'Bahrain',
  'Oman',
  'Qatar',
];

const REGIONS_BY_COUNTRY = {
  UAE: [
    'Abu Dhabi',
    'Dubai',
    'Sharjah',
    'Ajman',
    'Umm Al Quwain',
    'Ras Al Khaimah',
    'Fujairah',
  ],
  'Saudi Arabia': [
    'Riyadh',
    'Makkah',
    'Madinah',
    'Eastern Province',
    'Asir',
    'Tabuk',
    'Hail',
    'Northern Borders',
    'Jazan',
    'Najran',
    'Al Bahah',
    'Al Jouf',
    'Qassim',
  ],
};

const CURRENCIES = ['AED', 'SAR', 'KWD', 'BHD', 'OMR', 'QAR', 'USD'];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CONTENT_CATEGORIES = [
  'Baby Care & Parenting',
  'Family Lifestyle',
  'Mom Fashion & Beauty',
  'Healthy Cooking & Recipes',
  'Home & Decor',
  'Education & Learning',
  'Family Travel',
  'Fitness & Wellness',
  'Product Reviews',
  'DIY & Crafts',
];

const INSTAGRAM_FOLLOWERS = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+'];
const INSTAGRAM_STORY_VIEWS = ['<1K', '1K-4K', '4K-10K', '10K-25K', '25K-50K', '50K+'];
const INSTAGRAM_REEL_VIEWS = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K-500K', '500K+'];

const TIKTOK_FOLLOWERS = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+'];
const TIKTOK_VIDEO_VIEWS = ['<5K', '5K-25K', '25K-50K', '50K-250K', '250K+'];

const YOUTUBE_SUBSCRIBERS = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+'];
const YOUTUBE_VIDEO_VIEWS = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K-500K', '500K+'];

const SNAPCHAT_FOLLOWERS = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+'];
const SNAPCHAT_STORY_VIEWS = ['<500', '500-2K', '2K-10K', '10K-50K', '50K+'];

const OTHER_PLATFORM_OPTIONS = [
  'YouTube',
  'Snapchat',
  'Pinterest',
  'X (Twitter)',
  'LinkedIn',
  'Facebook',
  'Threads',
  'Podcast',
  'Other (specify)',
];
const OTHER_FOLLOWER_RANGES = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+'];

const PLATFORMS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'other', label: 'Other (Add specific platforms)' },
];

const emptyOtherPlatform = () => ({
  platform: '',
  platform_custom_name: '',
  username: '',
  followers_range: '',
});

const buildBirthYears = () => {
  const max = new Date().getFullYear() - 18;
  return Array.from({ length: max - 1960 + 1 }, (_, i) => max - i);
};

const buildChildBirthYears = () => {
  const current = new Date().getFullYear();
  return Array.from({ length: current - 1990 + 1 }, (_, i) => current - i);
};

const buildDueYears = () => {
  const current = new Date().getFullYear();
  return [current, current + 1];
};

const initialFormData = {
  influencer_type: '',

  full_name: '',
  email: '',
  phone_country_code: '+971',
  phone_number: '',
  country_of_residence: 'UAE',
  region: '',
  birth_year: '',

  children_count: '',
  children_birth_dates: [],
  is_currently_expecting: '',
  expected_due_month: '',
  expected_due_year: '',

  active_platforms: [],

  instagram_username: '',
  instagram_followers_range: '',
  instagram_story_views_range: '',
  instagram_reel_views_range: '',
  tiktok_username: '',
  tiktok_followers_range: '',
  tiktok_video_views_range: '',
  youtube_channel: '',
  youtube_subscribers_range: '',
  youtube_video_views_range: '',
  snapchat_username: '',
  snapchat_followers_range: '',
  snapchat_story_views_range: '',
  other_platforms: [],

  content_categories: [],
  currency: 'AED',
  instagram_story_price: '',
  instagram_reel_price: '',
  tiktok_video_price: '',
  snapchat_story_price: '',
  youtube_pricing_notes: '',

  privacy_consent: false,
};

const InfluencerForm = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState([]); // [{field, message}, ...]
  const [formData, setFormData] = useState(initialFormData);
  // UAE PDPL + GDPR consent (split: notice acknowledgement vs. explicit consent for sensitive data)
  const [noticeAcknowledged, setNoticeAcknowledged] = useState(false);
  const [explicitConsent, setExplicitConsent] = useState(false);

  const BIRTH_YEARS = buildBirthYears();
  const CHILD_BIRTH_YEARS = buildChildBirthYears();
  const DUE_YEARS = buildDueYears();

  // Auto-default currency when phone country code changes
  useEffect(() => {
    const mapped = COUNTRY_CODE_TO_CURRENCY[formData.phone_country_code];
    if (mapped) {
      setFormData(prev => ({ ...prev, currency: mapped }));
    }
  }, [formData.phone_country_code]);

  // Resize children_birth_dates array when children_count changes
  useEffect(() => {
    const n = parseInt(formData.children_count, 10);
    if (!Number.isNaN(n) && n > 0) {
      setFormData(prev => {
        const arr = [...(prev.children_birth_dates || [])];
        while (arr.length < n) arr.push({ month: '', year: '' });
        arr.length = n;
        return { ...prev, children_birth_dates: arr };
      });
    } else {
      setFormData(prev => ({ ...prev, children_birth_dates: [] }));
    }
  }, [formData.children_count]);

  // Reset region when switching to a country without regions
  useEffect(() => {
    if (!REGIONS_BY_COUNTRY[formData.country_of_residence]) {
      setFormData(prev => ({ ...prev, region: '' }));
    }
  }, [formData.country_of_residence]);

  // When 'other' becomes active and the list is empty, push one empty row.
  // When 'other' is deselected, clear the list.
  useEffect(() => {
    setFormData(prev => {
      const hasOther = prev.active_platforms.includes('other');
      if (hasOther && prev.other_platforms.length === 0) {
        return { ...prev, other_platforms: [emptyOtherPlatform()] };
      }
      if (!hasOther && prev.other_platforms.length > 0) {
        return { ...prev, other_platforms: [] };
      }
      return prev;
    });
  }, [formData.active_platforms]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Smart phone paste: detect GCC country codes, strip leading 0/00, keep digits only.
  // Idempotent — pasting the same value twice yields the same result.
  const KNOWN_COUNTRY_CODES = ['+971', '+966', '+965', '+974', '+973', '+968', '+90'];
  const handlePhoneChange = (e) => {
    let raw = e.target.value || '';
    // Keep only digits and "+"
    raw = raw.replace(/[^\d+]/g, '');
    // "00" international prefix → "+"
    if (raw.startsWith('00')) {
      raw = '+' + raw.substring(2);
    }
    // Country code detection (longest-first to avoid +90 swallowing +971)
    let detected = null;
    for (const code of KNOWN_COUNTRY_CODES.sort((a, b) => b.length - a.length)) {
      if (raw.startsWith(code)) {
        detected = code;
        raw = raw.substring(code.length);
        break;
      }
    }
    // Local format: "0501234567" → "501234567"
    if (raw.startsWith('0') && !raw.startsWith('+')) {
      raw = raw.substring(1);
    }
    // Strip any remaining "+"
    raw = raw.replace(/\D/g, '');

    setFormData(prev => ({
      ...prev,
      ...(detected ? { phone_country_code: detected } : {}),
      phone_number: raw,
    }));
  };

  const handleChildBirthChange = (index, field, value, key = 'children_birth_dates') => {
    setFormData(prev => {
      const arr = [...(prev[key] || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, [key]: arr };
    });
  };

  const toggleCategory = (category) => {
    setFormData(prev => {
      const exists = prev.content_categories.includes(category);
      return {
        ...prev,
        content_categories: exists
          ? prev.content_categories.filter(c => c !== category)
          : [...prev.content_categories, category],
      };
    });
  };

  const togglePlatform = (platformId) => {
    setFormData(prev => {
      const exists = prev.active_platforms.includes(platformId);
      return {
        ...prev,
        active_platforms: exists
          ? prev.active_platforms.filter(p => p !== platformId)
          : [...prev.active_platforms, platformId],
      };
    });
  };

  const updateOtherPlatform = (idx, field, value) => {
    setFormData(prev => {
      const arr = [...prev.other_platforms];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...prev, other_platforms: arr };
    });
  };

  const addOtherPlatform = () => {
    setFormData(prev => ({
      ...prev,
      other_platforms: [...prev.other_platforms, emptyOtherPlatform()],
    }));
  };

  const removeOtherPlatform = (idx) => {
    setFormData(prev => ({
      ...prev,
      other_platforms: prev.other_platforms.filter((_, i) => i !== idx),
    }));
  };

  const showRegion = !!REGIONS_BY_COUNTRY[formData.country_of_residence];
  const isMom = formData.influencer_type === 'Mom';
  const isMomToBe = formData.influencer_type === 'Mom-to-be';
  const isLifestyle = formData.influencer_type === 'Lifestyle';

  const hasIG = formData.active_platforms.includes('instagram');
  const hasTT = formData.active_platforms.includes('tiktok');
  const hasOther = formData.active_platforms.includes('other');

  const validate = () => {
    const errors = [];

    if (!formData.influencer_type) {
      errors.push({ field: 'influencer_type', message: 'Please select an influencer type' });
    }
    if (!formData.full_name || !formData.full_name.trim()) {
      errors.push({ field: 'full_name', message: 'Full name is required' });
    }
    if (!formData.email || !formData.email.trim()) {
      errors.push({ field: 'email', message: 'Email is required' });
    }
    const phoneDigits = (formData.phone_number || '').replace(/\D/g, '');
    if (!phoneDigits) {
      errors.push({ field: 'phone_number', message: 'Phone number is required' });
    } else if (phoneDigits.length < 7 || phoneDigits.length > 11) {
      errors.push({
        field: 'phone_number',
        message: 'Phone number length looks unusual. Please verify it has 7-11 digits.',
      });
    }
    if (!formData.country_of_residence) {
      errors.push({ field: 'country_of_residence', message: 'Country of residence is required' });
    }
    if (showRegion && !formData.region) {
      errors.push({ field: 'region', message: 'Region is required' });
    }
    if (!formData.birth_year) {
      errors.push({ field: 'birth_year', message: 'Birth year is required' });
    }

    if (isMom) {
      const n = parseInt(formData.children_count, 10);
      if (!n || n < 1) {
        errors.push({ field: 'children_count', message: 'Please enter the number of children' });
      }
      if (!formData.is_currently_expecting) {
        errors.push({ field: 'is_currently_expecting', message: 'Please indicate if you are currently expecting' });
      }
      if (formData.is_currently_expecting === 'yes' && (!formData.expected_due_month || !formData.expected_due_year)) {
        errors.push({ field: 'expected_due_month', message: 'Please select your expected due date' });
      }
    }
    if (isMomToBe) {
      if (!formData.expected_due_month || !formData.expected_due_year) {
        errors.push({ field: 'expected_due_month', message: 'Please select your expected due date' });
      }
    }

    if (formData.active_platforms.length < 1) {
      errors.push({ field: 'active_platforms', message: 'Please select at least one platform you are active on' });
    }
    if (hasOther && formData.other_platforms.length < 1) {
      errors.push({ field: 'other_platforms', message: 'Please add at least one other platform' });
    }
    if (formData.content_categories.length < 1) {
      errors.push({ field: 'content_categories', message: 'Please select at least one content category' });
    }
    if (!noticeAcknowledged) {
      errors.push({ field: 'notice_acknowledged', message: 'Please acknowledge the Privacy Policy' });
    }
    if (!explicitConsent) {
      errors.push({ field: 'explicit_consent', message: 'Please provide explicit consent for data processing' });
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const errors = validate();
    if (errors.length > 0) {
      setFormErrors(errors);
      scrollToFirstError(errors);
      return;
    }

    setFormErrors([]);
    setLoading(true);
    try {
      const payload = {
        influencer_type: formData.influencer_type,
        full_name: formData.full_name.trim(),
        email: formData.email.trim(),
        phone_country_code: formData.phone_country_code,
        phone_number: formData.phone_number.replace(/\D/g, ''),
        country_of_residence: formData.country_of_residence,
        region: showRegion ? formData.region : null,
        birth_year: parseInt(formData.birth_year, 10),

        children_count: isMom ? parseInt(formData.children_count, 10) : null,
        children_birth_dates: isMom
          ? formData.children_birth_dates.map(c => ({
              month: parseInt(c.month, 10),
              year: parseInt(c.year, 10),
            }))
          : null,

        is_currently_expecting: isMom
          ? formData.is_currently_expecting === 'yes'
          : null,
        expected_due_date:
          (isMomToBe ||
            (isMom && formData.is_currently_expecting === 'yes'))
            ? {
                month: parseInt(formData.expected_due_month, 10),
                year: parseInt(formData.expected_due_year, 10),
              }
            : null,

        active_platforms: formData.active_platforms,

        instagram_username: hasIG ? formData.instagram_username.trim() : null,
        instagram_followers_range: hasIG ? formData.instagram_followers_range : null,
        instagram_story_views_range: hasIG ? formData.instagram_story_views_range : null,
        instagram_reel_views_range: hasIG ? formData.instagram_reel_views_range : null,
        tiktok_username: hasTT ? formData.tiktok_username.trim() : null,
        tiktok_followers_range: hasTT ? formData.tiktok_followers_range : null,
        tiktok_video_views_range: hasTT ? formData.tiktok_video_views_range : null,
        other_platforms: hasOther
          ? formData.other_platforms.map(op => ({
              platform: op.platform,
              platform_custom_name:
                op.platform === 'Other (specify)' ? op.platform_custom_name.trim() : null,
              username: op.username.trim(),
              followers_range: op.followers_range,
            }))
          : [],

        content_categories: formData.content_categories,
        currency: formData.currency,
        instagram_story_price: hasIG && formData.instagram_story_price
          ? parseFloat(formData.instagram_story_price)
          : null,
        instagram_reel_price: hasIG && formData.instagram_reel_price
          ? parseFloat(formData.instagram_reel_price)
          : null,
        tiktok_video_price: hasTT && formData.tiktok_video_price
          ? parseFloat(formData.tiktok_video_price)
          : null,

        // UAE PDPL + GDPR audit trail
        notice_acknowledged: true,
        explicit_consent: true,
        consent_text_version: CURRENT_PRIVACY_POLICY_VERSION,
        consent_timestamp_client: new Date().toISOString(),
      };

      await axios.post(`${API}/influencer-signup`, payload);
      setSuccess(true);
      setFormData(initialFormData);
      setNoticeAcknowledged(false);
      setExplicitConsent(false);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      console.error('Form submission error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Lookup helper for inline field-level error messages.
  // formErrors stays as [{field, message}, ...]; this just sugar.
  const errorFor = (fieldName) => {
    const match = formErrors.find((e) => e.field === fieldName);
    return match ? match.message : null;
  };
  const fieldErrorCls = 'mt-1 text-xs text-red-400';

  const inputCls =
    'w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent disabled:opacity-40 disabled:cursor-not-allowed';
  const labelCls = 'block text-sm font-semibold text-gray-300 mb-2';
  const sectionTitleCls =
    'text-xl font-bold text-[#FFDD59] mb-4 pb-2 border-b border-gray-700';

  return (
    <section
      id="influencer-form"
      className="py-20 bg-black bg-opacity-75 border-t border-gray-800"
      data-testid="influencer-form-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Our Influencer Network
          </h2>
          <p className="text-base md:text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
            Fill out this form to join our influencer roster. We'll reach out via WhatsApp
            with collaboration opportunities matched to your profile.
          </p>
        </div>

        {error && (
          <div
            className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg"
            data-testid="error-message"
          >
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          noValidate
          className="bg-gray-900 border border-[#FFDD59] rounded-2xl shadow-xl p-8 space-y-8"
        >
          {/* Screen-reader announcement (WCAG 2.1 AA) */}
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {formErrors.length > 0
              ? `Form has ${formErrors.length} error${formErrors.length > 1 ? 's' : ''}. ${formErrors[0].message}`
              : ''}
          </div>

          {/* Visible error banner */}
          {formErrors.length > 0 && (
            <div className="bg-red-900/20 border border-red-500/40 rounded p-4 text-red-300" data-testid="form-errors-banner">
              <p className="font-semibold mb-2">
                Please fix the following {formErrors.length === 1 ? 'error' : 'errors'}:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                {formErrors.map((err, idx) => (
                  <li key={idx}>{err.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* === BÖLÜM 0: Influencer Type === */}
          <div>
            <h3 className={sectionTitleCls}>Tell Us About You</h3>
            <label className={labelCls}>
              Please select: <span className="text-red-500">*</span>
            </label>
            <div id="influencer_type" className="grid sm:grid-cols-3 gap-3" tabIndex="-1">
              {[
                { v: 'Mom', label: 'Mom', sub: 'I have a baby / child / children' },
                { v: 'Mom-to-be', label: 'Mom-to-be', sub: 'Expecting my first baby' },
                { v: 'Lifestyle', label: 'Lifestyle / Homemaker', sub: 'No children' },
              ].map(opt => (
                <label
                  key={opt.v}
                  className={`cursor-pointer p-4 rounded-lg border-2 transition ${
                    formData.influencer_type === opt.v
                      ? 'border-[#FFDD59] bg-gray-800'
                      : 'border-gray-700 bg-gray-800 hover:border-gray-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="influencer_type"
                    value={opt.v}
                    checked={formData.influencer_type === opt.v}
                    onChange={handleChange}
                    required
                    className="sr-only"
                    data-testid={`radio-influencer-type-${opt.v}`}
                  />
                  <div className="text-white font-semibold">{opt.label}</div>
                  <div className="text-gray-400 text-xs mt-1">{opt.sub}</div>
                </label>
              ))}
            </div>
            {errorFor('influencer_type') && (
              <p className={fieldErrorCls}>{errorFor('influencer_type')}</p>
            )}
          </div>

          {/* === BÖLÜM 1: Personal Info === */}
          {formData.influencer_type && (
            <div>
              <h3 className={sectionTitleCls}>Personal Info</h3>

              <div className="space-y-6">
                <div>
                  <label className={labelCls}>
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="full_name"
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    required
                    className={inputCls}
                    data-testid="input-full-name"
                  />
                  {errorFor('full_name') && (
                    <p className={fieldErrorCls}>{errorFor('full_name')}</p>
                  )}
                </div>

                <div>
                  <label className={labelCls}>
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={inputCls}
                    data-testid="input-email"
                  />
                  {errorFor('email') && (
                    <p className={fieldErrorCls}>{errorFor('email')}</p>
                  )}
                </div>

                <div>
                  <label className={labelCls}>
                    Your Phone Number (WhatsApp) <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <select
                      name="phone_country_code"
                      value={formData.phone_country_code}
                      onChange={handleChange}
                      className={`${inputCls} max-w-[140px]`}
                      data-testid="select-phone-country-code"
                    >
                      {COUNTRY_CODES.map(c => (
                        <option key={c.code} value={c.code}>
                          {c.flag} {c.code}
                        </option>
                      ))}
                    </select>
                    <input
                      id="phone_number"
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handlePhoneChange}
                      placeholder="50 123 4567"
                      className={inputCls}
                      data-testid="input-phone-number"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Enter your number without country code. We'll auto-detect if you paste with country code.
                    We use WhatsApp for all communication.
                  </p>
                  {errorFor('phone_number') && (
                    <p className={fieldErrorCls}>{errorFor('phone_number')}</p>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls}>
                      Country of Residence <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="country_of_residence"
                      name="country_of_residence"
                      value={formData.country_of_residence}
                      onChange={handleChange}
                      required
                      className={inputCls}
                      data-testid="select-country"
                    >
                      {COUNTRIES_OF_RESIDENCE.map(c => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    {errorFor('country_of_residence') && (
                      <p className={fieldErrorCls}>{errorFor('country_of_residence')}</p>
                    )}
                  </div>

                  {showRegion && (
                    <div>
                      <label className={labelCls}>
                        Region <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="region"
                        name="region"
                        value={formData.region}
                        onChange={handleChange}
                        required
                        className={inputCls}
                        data-testid="select-region"
                      >
                        <option value="">Select region</option>
                        {REGIONS_BY_COUNTRY[formData.country_of_residence].map(r => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                      {errorFor('region') && (
                        <p className={fieldErrorCls}>{errorFor('region')}</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelCls}>
                    Birth Year <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="birth_year"
                    name="birth_year"
                    value={formData.birth_year}
                    onChange={handleChange}
                    required
                    className={inputCls}
                    data-testid="select-birth-year"
                  >
                    <option value="">Select year</option>
                    {BIRTH_YEARS.map(y => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  {errorFor('birth_year') && (
                    <p className={fieldErrorCls}>{errorFor('birth_year')}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* === BÖLÜM 2: Family Info === */}
          {isMom && (
            <div>
              <h3 className={sectionTitleCls}>Family Info</h3>
              <div className="space-y-6 bg-gray-800 p-6 rounded-lg border border-gray-700">
                <div>
                  <label className={labelCls}>
                    How many children do you have? <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="children_count"
                    type="number"
                    name="children_count"
                    min="1"
                    max="20"
                    value={formData.children_count}
                    onChange={handleChange}
                    required
                    className={inputCls}
                    data-testid="input-children-count"
                  />
                  {errorFor('children_count') && (
                    <p className={fieldErrorCls}>{errorFor('children_count')}</p>
                  )}
                </div>

                {formData.children_birth_dates.map((child, idx) => (
                  <div key={idx}>
                    <label className={labelCls}>
                      Birth month + year of child #{idx + 1}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <select
                        value={child.month}
                        onChange={(e) =>
                          handleChildBirthChange(idx, 'month', e.target.value)
                        }
                        required
                        className={inputCls}
                        data-testid={`select-child-${idx}-month`}
                      >
                        <option value="">Month</option>
                        {MONTHS.map((m, i) => (
                          <option key={m} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <select
                        value={child.year}
                        onChange={(e) =>
                          handleChildBirthChange(idx, 'year', e.target.value)
                        }
                        required
                        className={inputCls}
                        data-testid={`select-child-${idx}-year`}
                      >
                        <option value="">Year</option>
                        {CHILD_BIRTH_YEARS.map(y => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}

                <div>
                  <label className={labelCls}>
                    Are you currently expecting a new baby?{' '}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="is_currently_expecting"
                    name="is_currently_expecting"
                    value={formData.is_currently_expecting}
                    onChange={handleChange}
                    required
                    className={inputCls}
                    data-testid="select-is-currently-expecting"
                  >
                    <option value="">Select</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                  {errorFor('is_currently_expecting') && (
                    <p className={fieldErrorCls}>{errorFor('is_currently_expecting')}</p>
                  )}
                </div>

                {formData.is_currently_expecting === 'yes' && (
                  <div>
                    <label className={labelCls}>
                      Expected due month + year{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <select
                        id="expected_due_month"
                        name="expected_due_month"
                        value={formData.expected_due_month}
                        onChange={handleChange}
                        required
                        className={inputCls}
                        data-testid="select-mom-due-month"
                      >
                        <option value="">Month</option>
                        {MONTHS.map((m, i) => (
                          <option key={m} value={i + 1}>
                            {m}
                          </option>
                        ))}
                      </select>
                      <select
                        name="expected_due_year"
                        value={formData.expected_due_year}
                        onChange={handleChange}
                        required
                        className={inputCls}
                        data-testid="select-mom-due-year"
                      >
                        <option value="">Year</option>
                        {DUE_YEARS.map(y => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>
                    {errorFor('expected_due_month') && (
                      <p className={fieldErrorCls}>{errorFor('expected_due_month')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {isMomToBe && (
            <div>
              <h3 className={sectionTitleCls}>Family Info</h3>
              <div className="space-y-6 bg-gray-800 p-6 rounded-lg border border-gray-700">
                <div>
                  <label className={labelCls}>
                    Expected due month + year <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <select
                      id="expected_due_month"
                      name="expected_due_month"
                      value={formData.expected_due_month}
                      onChange={handleChange}
                      required
                      className={inputCls}
                      data-testid="select-due-month"
                    >
                      <option value="">Month</option>
                      {MONTHS.map((m, i) => (
                        <option key={m} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <select
                      name="expected_due_year"
                      value={formData.expected_due_year}
                      onChange={handleChange}
                      required
                      className={inputCls}
                      data-testid="select-due-year"
                    >
                      <option value="">Year</option>
                      {DUE_YEARS.map(y => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errorFor('expected_due_month') && (
                    <p className={fieldErrorCls}>{errorFor('expected_due_month')}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* === BÖLÜM 3: Social Media === */}
          {formData.influencer_type && (
            <div>
              <h3 className={sectionTitleCls}>Social Media</h3>

              {/* Platform multi-select */}
              <div className="mb-6">
                <label className={labelCls}>
                  Which platforms are you active on?{' '}
                  <span className="text-red-500">*</span>{' '}
                  <span className="text-xs text-gray-400 font-normal">(select at least one)</span>
                </label>
                <div id="active_platforms" className="grid sm:grid-cols-2 gap-2" tabIndex="-1">
                  {PLATFORMS.map(p => {
                    const checked = formData.active_platforms.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                          checked
                            ? 'border-[#FFDD59] bg-gray-800'
                            : 'border-gray-700 bg-gray-800 hover:border-gray-500'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePlatform(p.id)}
                          className="w-4 h-4 accent-[#FFDD59]"
                          data-testid={`checkbox-platform-${p.id}`}
                        />
                        <span className="text-sm text-white">{p.label}</span>
                      </label>
                    );
                  })}
                </div>
                {errorFor('active_platforms') && (
                  <p className={fieldErrorCls}>{errorFor('active_platforms')}</p>
                )}
              </div>

              {/* Instagram card */}
              {hasIG && (
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-4">
                  <div className="text-lg font-semibold text-white mb-4">
                    📷 Instagram
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className={labelCls}>
                        Username (without @) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="instagram_username"
                        value={formData.instagram_username}
                        onChange={handleChange}
                        placeholder="yourusername"
                        required
                        className={inputCls}
                        data-testid="input-instagram-username"
                      />
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className={labelCls}>
                          Follower Count <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="instagram_followers_range"
                          value={formData.instagram_followers_range}
                          onChange={handleChange}
                          required
                          className={inputCls}
                        >
                          <option value="">Select</option>
                          {INSTAGRAM_FOLLOWERS.map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>
                          Avg. Story Views <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="instagram_story_views_range"
                          value={formData.instagram_story_views_range}
                          onChange={handleChange}
                          required
                          className={inputCls}
                        >
                          <option value="">Select</option>
                          {INSTAGRAM_STORY_VIEWS.map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>
                          Avg. Reel Views <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="instagram_reel_views_range"
                          value={formData.instagram_reel_views_range}
                          onChange={handleChange}
                          required
                          className={inputCls}
                        >
                          <option value="">Select</option>
                          {INSTAGRAM_REEL_VIEWS.map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* TikTok card */}
              {hasTT && (
                <div className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-4">
                  <div className="text-lg font-semibold text-white mb-4">
                    🎵 TikTok
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className={labelCls}>
                        Username (without @) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="tiktok_username"
                        value={formData.tiktok_username}
                        onChange={handleChange}
                        placeholder="yourusername"
                        required
                        className={inputCls}
                        data-testid="input-tiktok-username"
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>
                          Follower Count <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="tiktok_followers_range"
                          value={formData.tiktok_followers_range}
                          onChange={handleChange}
                          required
                          className={inputCls}
                        >
                          <option value="">Select</option>
                          {TIKTOK_FOLLOWERS.map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>
                          Avg. Video Views <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="tiktok_video_views_range"
                          value={formData.tiktok_video_views_range}
                          onChange={handleChange}
                          required
                          className={inputCls}
                        >
                          <option value="">Select</option>
                          {TIKTOK_VIDEO_VIEWS.map(o => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Other Platforms (dynamic list) */}
              {hasOther && (
                <div id="other_platforms" tabIndex="-1" className="bg-gray-800 p-6 rounded-lg border border-gray-700 mb-4">
                  <div className="text-lg font-semibold text-white mb-4">
                    🌐 Other Platforms
                  </div>
                  <div className="space-y-4">
                    {formData.other_platforms.map((op, idx) => {
                      const isCustom = op.platform === 'Other (specify)';
                      return (
                        <div
                          key={idx}
                          className="bg-gray-900 border border-gray-700 rounded-lg p-4 space-y-3"
                          data-testid={`other-platform-card-${idx}`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-[#FFDD59]">
                              Platform #{idx + 1}
                            </span>
                            {formData.other_platforms.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeOtherPlatform(idx)}
                                className="text-xs text-red-400 hover:text-red-300 font-semibold"
                                data-testid={`remove-other-platform-${idx}`}
                              >
                                × Remove
                              </button>
                            )}
                          </div>

                          <div>
                            <label className={labelCls}>
                              Platform <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={op.platform}
                              onChange={(e) =>
                                updateOtherPlatform(idx, 'platform', e.target.value)
                              }
                              required
                              className={inputCls}
                              data-testid={`select-other-platform-${idx}`}
                            >
                              <option value="">Select platform</option>
                              {OTHER_PLATFORM_OPTIONS.map(o => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          </div>

                          {isCustom && (
                            <div>
                              <label className={labelCls}>
                                Platform name <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={op.platform_custom_name}
                                onChange={(e) =>
                                  updateOtherPlatform(idx, 'platform_custom_name', e.target.value)
                                }
                                placeholder="e.g. Twitch, Reddit"
                                required
                                className={inputCls}
                              />
                            </div>
                          )}

                          <div>
                            <label className={labelCls}>
                              Username / Profile URL <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={op.username}
                              onChange={(e) =>
                                updateOtherPlatform(idx, 'username', e.target.value)
                              }
                              placeholder="@username or full URL"
                              required
                              className={inputCls}
                            />
                          </div>

                          <div>
                            <label className={labelCls}>
                              Follower Count <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={op.followers_range}
                              onChange={(e) =>
                                updateOtherPlatform(idx, 'followers_range', e.target.value)
                              }
                              required
                              className={inputCls}
                            >
                              <option value="">Select</option>
                              {OTHER_FOLLOWER_RANGES.map(o => (
                                <option key={o} value={o}>{o}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      onClick={addOtherPlatform}
                      className="w-full py-3 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition"
                      data-testid="add-other-platform"
                    >
                      + Add Another Platform
                    </button>
                  </div>
                  {errorFor('other_platforms') && (
                    <p className={fieldErrorCls}>{errorFor('other_platforms')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* === BÖLÜM 4: Content Categories & Pricing === */}
          {formData.influencer_type && (
            <div>
              <h3 className={sectionTitleCls}>Content & Pricing</h3>

              <div className="mb-6">
                <label className={labelCls}>
                  Which categories do you create content in? <span className="text-red-500">*</span>{' '}
                  <span className="text-xs text-gray-400 font-normal">(select at least one)</span>
                </label>
                <div id="content_categories" className="grid sm:grid-cols-2 gap-2" tabIndex="-1">
                  {CONTENT_CATEGORIES.map(cat => {
                    const checked = formData.content_categories.includes(cat);
                    return (
                      <label
                        key={cat}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                          checked
                            ? 'border-[#FFDD59] bg-gray-800'
                            : 'border-gray-700 bg-gray-800 hover:border-gray-500'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCategory(cat)}
                          className="w-4 h-4 accent-[#FFDD59]"
                          data-testid={`checkbox-category-${cat}`}
                        />
                        <span className="text-sm text-white">{cat}</span>
                      </label>
                    );
                  })}
                </div>
                {errorFor('content_categories') && (
                  <p className={fieldErrorCls}>{errorFor('content_categories')}</p>
                )}
              </div>

              <div className="mb-6">
                <label className={labelCls}>
                  Currency <span className="text-red-500">*</span>
                </label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  required
                  className={inputCls}
                  data-testid="select-currency"
                >
                  {CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {(hasIG || hasTT) && (
                <div className="bg-black border border-[#FFDD59] p-6 rounded-lg space-y-4">
                  <h4 className="text-lg font-bold text-[#FFDD59]">Budget Expectations</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {hasIG && (
                        <div>
                          <label className={labelCls}>
                            Instagram - Story Price <span className="text-red-500">*</span>
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              name="instagram_story_price"
                              value={formData.instagram_story_price}
                              onChange={handleChange}
                              required={hasIG}
                              className={`${inputCls} rounded-r-none`}
                            />
                            <span className="px-3 py-3 bg-gray-700 border border-l-0 border-gray-600 text-gray-300 rounded-r-lg text-sm font-semibold">
                              {formData.currency}
                            </span>
                          </div>
                        </div>
                      )}
                      {hasIG && (
                        <div>
                          <label className={labelCls}>
                            Instagram - Reel Price <span className="text-red-500">*</span>
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              name="instagram_reel_price"
                              value={formData.instagram_reel_price}
                              onChange={handleChange}
                              required={hasIG}
                              className={`${inputCls} rounded-r-none`}
                            />
                            <span className="px-3 py-3 bg-gray-700 border border-l-0 border-gray-600 text-gray-300 rounded-r-lg text-sm font-semibold">
                              {formData.currency}
                            </span>
                          </div>
                        </div>
                      )}
                      {hasTT && (
                        <div>
                          <label className={labelCls}>
                            TikTok - Video Price <span className="text-red-500">*</span>
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              name="tiktok_video_price"
                              value={formData.tiktok_video_price}
                              onChange={handleChange}
                              required={hasTT}
                              className={`${inputCls} rounded-r-none`}
                            />
                            <span className="px-3 py-3 bg-gray-700 border border-l-0 border-gray-600 text-gray-300 rounded-r-lg text-sm font-semibold">
                              {formData.currency}
                            </span>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* === BÖLÜM 5: UAE PDPL + GDPR consent (notice + explicit) === */}
          {formData.influencer_type && (
            <div className="bg-yellow-50/10 border border-[#FFDD59]/40 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer mb-3">
                <input
                  id="notice_acknowledged"
                  type="checkbox"
                  checked={noticeAcknowledged}
                  onChange={(e) => setNoticeAcknowledged(e.target.checked)}
                  aria-required="true"
                  autoComplete="off"
                  className="mt-0.5 w-4 h-4 accent-[#FFDD59]"
                  data-testid="checkbox-notice-acknowledged"
                />
                <span className="text-sm text-gray-300">
                  I have read and understood the{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#FFDD59] underline hover:text-yellow-300"
                  >
                    Persona Privacy Policy
                  </a>
                  . <span className="text-red-500">*</span>
                </span>
              </label>
              {errorFor('notice_acknowledged') && (
                <p className={fieldErrorCls + ' ml-7'}>{errorFor('notice_acknowledged')}</p>
              )}

              <label className="flex items-start gap-3 cursor-pointer mt-3">
                <input
                  id="explicit_consent"
                  type="checkbox"
                  checked={explicitConsent}
                  onChange={(e) => setExplicitConsent(e.target.checked)}
                  aria-required="true"
                  autoComplete="off"
                  className="mt-0.5 w-4 h-4 accent-[#FFDD59]"
                  data-testid="checkbox-explicit-consent"
                />
                <span className="text-sm text-gray-300">
                  I give my explicit consent for the processing of my personal data, including
                  special categories such as pregnancy and family information, for the purpose of
                  influencer-brand campaign matching.{' '}
                  <span className="text-red-500">*</span>
                </span>
              </label>
              {errorFor('explicit_consent') && (
                <p className={fieldErrorCls + ' ml-7'}>{errorFor('explicit_consent')}</p>
              )}
            </div>
          )}

          {success && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
              role="status"
              aria-live="polite"
              data-testid="success-message"
            >
              <div className="bg-green-500/95 text-white px-8 py-6 rounded-xl shadow-2xl max-w-sm w-full text-center">
                <Check size={48} className="mx-auto mb-3" />
                <p className="text-lg font-semibold">
                  Your application has been received!
                </p>
                <p className="text-sm mt-1 text-green-50/90">
                  We will contact you on WhatsApp soon.
                </p>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Submitting...
              </>
            ) : (
              'Submit My Application'
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default InfluencerForm;
